import { useEffect, useState } from 'react'
import BookMineListRange from './BookMineListRange'
import BookResponse from '../BookResponse'
import { BooksitoutServer } from '../../../config/BooksitoutServer'
import Paging from '../../../common/hooks/Paging'
import BooksByYear from './BooksByYear'

const useMineBookList = (range: BookMineListRange | null, query: string = '', readingSourceProviders: string[] = [], refreshKey: number = 0) => {
	const [page, setPage] = useState<number>(1)
	const size = 24
	const [isLast, setIsLast] = useState<boolean>(false)
	const [totalPages, setTotalPages] = useState<number>(0)
	const [totalCount, setTotalCount] = useState<number>(0)
	const [isFirstFetch, setIsFirstFetch] = useState<boolean>(true)
	const [isLoading, setIsLoading] = useState<boolean>(true)
	const [fetchKey, setFetchKey] = useState<number>(0)

	const [books, setBooks] = useState<BookResponse[]>([])
	const [booksByYear, setBooksByYear] = useState<BooksByYear>({})

	useEffect(() => {
		if (range != null) {
			const queryParam = query ? `&q=${encodeURIComponent(query)}` : ''
			const sourceParam = readingSourceProviders.map(p => `&readingSourceProviders=${p}`).join('')
			BooksitoutServer
				.get(`/v1/book/my-book?range=${range}&page=${page}&size=${size}${queryParam}${sourceParam}`)
				.then(res => {
					setBooks(prevBook => {
					if (page === 1) return res.data.content
					const existingIds = new Set(prevBook.map(b => b.id))
					const newBooks = res.data.content.filter((b: BookResponse) => !existingIds.has(b.id))
					return [...prevBook, ...newBooks]
				})
					setIsLast(res.data.isLast)
					setTotalPages(res.data.totalPages)
					setTotalCount(res.data.totalCount ?? 0)

					if (range === BookMineListRange.DONE) {
						const allBooks = page === 1 ? res.data.content : [...books, ...res.data.content]
						setBooksByYear(groupBooksByYear(allBooks, page === 1 ? {} : booksByYear));
					}
				})
				.finally(() => {
					setIsFirstFetch(false)
					setIsLoading(false)
				})
		}
	}, [page, fetchKey])

	useEffect(() => {
		if (!isFirstFetch) {
			setPage(1)
			setIsLast(false)
			setIsLoading(true)
			setBooks([])
			setBooksByYear({})
			setFetchKey(prev => prev + 1)
		}
	}, [range, query, readingSourceProviders.join(','), refreshKey])

	const fetchNext = () => {
		setPage(prev => prev + 1)
	}

	const paging: Paging = {
		hasMore: !isLast,
		totalPages: totalPages,
		fetchNext: fetchNext,
	}

	const groupBooksByYear = (books: BookResponse[], existingGroups: BooksByYear): BooksByYear => {
		const existingBookId = existingGroups ? Object.values(existingGroups).flat().map(book => book.id) : []

		return books.reduce((acc: BooksByYear, book: BookResponse) => {
			if (!book.doneYear) return acc

			const year = book.doneYear

			if (!existingBookId.includes(book.id)) {
				acc[year] = acc[year] || []
				acc[year].push(book)
			}

			return acc
		}, existingGroups)
	}

	return [isLoading, books, booksByYear, paging, totalCount] as const
}

export default useMineBookList
